<template>
  <q-layout view="hHh lpr fFf">
    <!-- dialogs -->
    <UserManagementLocal />
    <PassageDialog />
    <AddDevice />
    <RemoveDevice />
    <ChangePassword />
    <QueueCommands />
    <QueueIridiumCommands />
    <DeviceHistory />
    <NotificationSettings />
    <RealTimeStream
      v-for="uid in openStreamUids"
      :key="uid"
      :stream-uid="uid"
      :is-active="uid === activeStreamUid"
      @stream-closed="onStreamClosed"
    />
    <RealTimeOptions />
    <PipelineSectionManager v-model="managePipelineSectionDialog" />

    <q-header elevated class="basic" style="background-color: #24337a">
      <q-toolbar class="bg-blue-brand text-white">
        <q-btn
          flat
          dense
          round
          icon="menu"
          class="lt-md q-mr-sm"
          aria-label="Toggle menu"
          @click="openDrawer"
        />
        <img
          src="PropipeNorthAmericaLogo-WhiteRed.png"
          title="ProPipe North America"
          style="height: 40px"
        />

        <div class="gt-sm q-ml-md text-red" style="font-size: 12px">
          SMS text messaging alerts now require opt-in permission. Sign up
          through the user menu.
        </div>

        <q-space />

        <span class="text-white q-mr-md" style="font-size: 12px">
          <span ref="utcClockText"></span>
          <q-tooltip
            anchor="bottom middle"
            self="top middle"
            :offset="[0, 8]"
            max-width="280px"
          >
            This is your current local time converted to UTC (Coordinated
            Universal Time), the global standard time zone used to keep all
            systems synchronized regardless of your location.
          </q-tooltip>
        </span>

        <q-btn
          v-if="isWorksheetDrawerMobile"
          flat
          dense
          round
          :icon="worksheetMobileSheetOpen ? 'close' : 'table_chart'"
          :aria-label="
            worksheetMobileSheetOpen ? 'Close tracksheet' : 'Open tracksheet'
          "
          :aria-expanded="worksheetMobileSheetOpen"
          @click="toggleMobileWorksheetSheet"
        >
          <q-tooltip>
            {{
              worksheetMobileSheetOpen ? 'Close tracksheet' : 'Open tracksheet'
            }}
          </q-tooltip>
        </q-btn>

        <q-btn round flat icon="more_vert">
          <q-menu>
            <q-list style="min-width: 200px">
              <q-item
                v-if="userHasAnyRole('admin', 'superadmin')"
                v-close-popup
                clickable
                @click="onUserManager"
              >
                <q-item-section>User Manager</q-item-section>
              </q-item>
              <q-item
                v-if="userHasAnyRole('admin', 'superadmin')"
                v-close-popup
                clickable
                @click="onStreamManagerClick"
              >
                <q-item-section>Stream Manager</q-item-section>
              </q-item>
              <q-item
                v-if="userHasAnyRole('superadmin')"
                v-close-popup
                clickable
                @click="onGroupAssignment"
              >
                <q-item-section>Group Assignment</q-item-section>
              </q-item>
              <q-item
                v-if="
                  userHasAnyRole('metrics') &&
                  userHasAnyRole('admin', 'superadmin')
                "
                v-close-popup
                clickable
                @click="onSystemMetrics"
              >
                <q-item-section>System Metrics</q-item-section>
              </q-item>
              <q-item
                v-if="userHasAnyRole('moderator')"
                v-close-popup
                clickable
                :to="{ name: 'moderation' }"
              >
                <q-item-section>Moderation</q-item-section>
              </q-item>
              <q-item
                v-if="userHasAnyRole('admin') && !userHasAnyRole('superadmin')"
                v-close-popup
                clickable
                @click="onPipelineSectionManager"
              >
                <q-item-section>Pipeline Manager</q-item-section>
              </q-item>
              <q-item
                v-if="userHasAnyRole('admin') && !userHasAnyRole('superadmin')"
                v-close-popup
                clickable
                @click="onPigRunManagerWizard"
              >
                <q-item-section>Pig Run Manager</q-item-section>
              </q-item>
              <q-item
                v-if="userHasAnyRole('admin')"
                v-close-popup
                clickable
                @click="onDownloadManagerClick"
              >
                <q-item-section>Download Manager</q-item-section>
              </q-item>
              <q-item
                v-if="userHasAnyRole('admin')"
                v-close-popup
                clickable
                @click="onReportManagerClick"
              >
                <q-item-section>Report Manager</q-item-section>
              </q-item>
              <q-item
                v-if="userHasAnyRole('admin')"
                v-close-popup
                clickable
                @click="onBroadcastCommandsClick"
              >
                <q-item-section>Broadcast Commands</q-item-section>
              </q-item>
              <q-item v-close-popup clickable @click="onNotificationSettings">
                <q-item-section>Notifications / SMS Opt-in</q-item-section>
              </q-item>
              <q-item v-close-popup clickable @click="onServiceStatus">
                <q-item-section>Service Status</q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                role="link"
                :to="{ name: 'supportLibrary' }"
              >
                <q-item-section>Technical Support</q-item-section>
              </q-item>
              <q-separator />
              <q-item v-close-popup clickable @click="onChangePassword">
                <q-item-section>Change Password</q-item-section>
              </q-item>
              <q-separator />
              <q-item v-close-popup clickable @click="onLogout">
                <q-item-section>Logout</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </q-toolbar>
    </q-header>

    <q-drawer
      v-model="leftDrawerOpen"
      show-if-above
      bordered
      :width="leftDrawerCurrentWidth"
      content-class="bg-grey-2"
      @hide="drawerToggle"
      @show="drawerToggle"
      @on-layout="leftDrawerOnLayout = $event"
    >
      <div class="drawer-wrapper">
        <div
          v-show="!chatExpanded"
          class="row q-pa-xs"
          :class="drawerMini ? 'justify-center' : 'justify-end'"
        >
          <q-btn
            flat
            dense
            round
            :icon="drawerMini ? 'chevron_right' : 'chevron_left'"
            :aria-label="drawerMini ? 'Expand drawer' : 'Collapse drawer'"
            @click="toggleDrawer"
          >
            <q-tooltip>{{
              drawerMini ? 'Expand menu' : 'Collapse menu'
            }}</q-tooltip>
          </q-btn>
        </div>
        <template v-if="!drawerMini">
          <div v-show="!chatExpanded" class="drawer-panels">
            <DeviceInfo />
            <DeviceTable />
            <PigRunManager />
            <ReportManager />
            <DownloadManager />
            <BroadcastCommands />
            <StreamManager />
          </div>
          <GroupChat
            v-if="userHasAnyRole('admin', 'superadmin')"
            @update:expanded="chatExpanded = $event"
          />
        </template>
      </div>
    </q-drawer>

    <q-drawer
      v-if="!isWorksheetDrawerMobile"
      v-model="worksheetDrawerOpen"
      side="right"
      behavior="desktop"
      bordered
      :width="worksheetDrawerWidth"
      content-class="bg-grey-1 tracking-worksheet-drawer"
      @hide="onTrackingWorksheetDrawerHide"
    >
      <div
        class="tracking-worksheet-resize-handle"
        :class="{
          'tracking-worksheet-resize-handle--active':
            worksheetDrawerResize !== null,
        }"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize tracksheet"
        :aria-valuemin="worksheetDrawerMinimumWidth"
        :aria-valuemax="worksheetDrawerMaximumWidth"
        :aria-valuenow="worksheetDrawerWidth"
        tabindex="0"
        @pointerdown="startWorksheetDrawerResize"
        @dblclick="resetWorksheetDrawerWidth"
        @keydown="onWorksheetDrawerResizeKeydown"
      >
        <q-tooltip>Drag to resize tracksheet. Double-click to reset.</q-tooltip>
      </div>
      <div class="row q-pa-xs justify-start">
        <q-btn
          flat
          dense
          round
          icon="chevron_right"
          aria-label="Collapse tracksheet"
          @click="closeWorksheetDrawer"
        >
          <q-tooltip>Collapse tracksheet</q-tooltip>
        </q-btn>
      </div>
      <PigRunTrackingWorksheet
        v-if="worksheetContentMounted"
        :entry="selectedPigRunEntry"
        :completed-pig-run-id="selectedCompletedPigRunId"
        @show-marker-on-map="onShowMarkerOnMap"
      />
    </q-drawer>

    <q-dialog
      v-if="isWorksheetDrawerMobile"
      v-model="worksheetMobileSheetOpen"
      maximized
      transition-show="slide-up"
      transition-hide="slide-down"
      @hide="onTrackingWorksheetMobileSheetHide"
    >
      <q-card class="tracking-worksheet-sheet bg-grey-1">
        <div
          class="tracking-worksheet-mobile-header row items-center no-wrap q-px-sm"
        >
          <div class="text-subtitle2 text-weight-medium text-grey-9">
            Tracksheet
          </div>
          <q-space />
          <q-btn
            flat
            dense
            round
            icon="close"
            aria-label="Close tracksheet"
            @click="closeMobileWorksheetSheet"
          >
            <q-tooltip>Close tracksheet</q-tooltip>
          </q-btn>
        </div>
        <PigRunTrackingWorksheet
          v-if="worksheetContentMounted"
          class="tracking-worksheet-mobile-content"
          :entry="selectedPigRunEntry"
          :completed-pig-run-id="selectedCompletedPigRunId"
          @show-marker-on-map="onShowMarkerOnMap"
        />
      </q-card>
    </q-dialog>

    <q-page-container>
      <Map />
      <div
        v-if="!isWorksheetDrawerMobile"
        class="tracking-worksheet-toggle-overlay"
        :style="trackingWorksheetToggleStyle"
      >
        <q-btn
          unelevated
          dense
          no-caps
          icon="table_chart"
          label="Tracksheet"
          class="tracking-worksheet-toggle"
          :color="isWorksheetDrawerOpen ? 'primary' : 'white'"
          :text-color="isWorksheetDrawerOpen ? 'white' : 'grey-8'"
          :aria-label="
            isWorksheetDrawerOpen ? 'Close tracksheet' : 'Open tracksheet'
          "
          :aria-pressed="isWorksheetDrawerOpen"
          @click="toggleWorksheetDrawer"
        />
      </div>
    </q-page-container>

    <div v-if="devicesReady && selectedPigRun">
      <PigProgressCard
        :key="selectedPigRun.pigRunId"
        :pig-run="selectedPigRun"
        :position="0"
        :horizontal-offset="pigProgressCardOffset"
        @width-change="pigProgressCardWidth = $event"
      />
    </div>
  </q-layout>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent } from 'vue';
import DeviceInfo from '@/components/DeviceInfo.vue';
import DeviceTable from '@/components/DeviceTable.vue';
import PigRunManager from '@/components/PigRunManager.vue';
import PigProgressCard from '@/components/PigProgressCard.vue';
import Map from '@/views/Map.vue';
import ReportManager from '@/components/ReportManager.vue';
import DownloadManager from '@/components/DownloadManager.vue';
import BroadcastCommands from '@/components/BroadcastCommands.vue';
import StreamManager from '@/components/StreamManager.vue';
import GroupChat from '@/components/GroupChat.vue';
import { hasAnyRole, hasAllRoles } from '@/utils/AuthGuard';
import MessageHub from '@/services/MessageHub';
import StreamingHub from '@/services/StreamingHub';
import GroupChatHub from '@/services/GroupChatHub';
import type { PigRunMapEntry, SelectedMarker } from '@/models/PigRunState';
import PigRunTrackingWorksheet from '@/components/PigRunTrackingWorksheet.vue';
import {
  leftDrawerCurrentWidth,
  mapCenterOffset,
  PIG_PROGRESS_CARD_MAX_WIDTH,
} from '@/utils/PigProgressCardLayout';

// Lazy-load dialog/modal components — they aren't visible on first paint
// and don't need to block the initial render.
const AddDevice = defineAsyncComponent(
  () => import('@/components/AddDevice.vue')
);
const RemoveDevice = defineAsyncComponent(
  () => import('@/components/RemoveDevice.vue')
);
const ChangePassword = defineAsyncComponent(
  () => import('@/components/ChangePassword.vue')
);
const QueueCommands = defineAsyncComponent(
  () => import('@/components/QueueCommands.vue')
);
const QueueIridiumCommands = defineAsyncComponent(
  () => import('@/components/QueueIridiumCommands.vue')
);
const DeviceHistory = defineAsyncComponent(
  () => import('@/components/DeviceHistory.vue')
);
const PassageDialog = defineAsyncComponent(
  () => import('@/components/PassageDialog.vue')
);
const NotificationSettings = defineAsyncComponent(
  () => import('@/components/NotificationSettings.vue')
);
const RealTimeStream = defineAsyncComponent(
  () => import('@/components/RealTimeStream.vue')
);
const UserManagementLocal = defineAsyncComponent(
  () => import('@/components/UserManagementLocal.vue')
);
const RealTimeOptions = defineAsyncComponent(
  () => import('@/components/RealTimeOptions.vue')
);
const PipelineSectionManager = defineAsyncComponent(
  () => import('@/components/PipelineSectionManager.vue')
);

const TRACKING_WORKSHEET_DRAWER_MIN_WIDTH = 440;
const TRACKING_WORKSHEET_DRAWER_MIN_MAP_WIDTH = 360;
const TRACKING_WORKSHEET_DRAWER_RESIZE_STEP = 24;

export default defineComponent({
  name: 'LayoutDefault',
  components: {
    AddDevice,
    RemoveDevice,
    ChangePassword,
    DeviceInfo,
    DeviceTable,
    QueueCommands,
    QueueIridiumCommands,
    DeviceHistory,
    PassageDialog,
    PigRunManager,
    PigProgressCard,
    NotificationSettings,
    RealTimeStream,
    UserManagementLocal,
    RealTimeOptions,
    Map,
    ReportManager,
    DownloadManager,
    BroadcastCommands,
    StreamManager,
    PipelineSectionManager,
    GroupChat,
    PigRunTrackingWorksheet,
  },
  data() {
    return {
      leftDrawerOpen: this.$store.state.leftDrawerOpen,
      drawerMini: false,
      leftDrawerOnLayout: false,
      pigProgressCardWidth: PIG_PROGRESS_CARD_MAX_WIDTH,
      devicesReady: false,
      startupComplete: false,
      managePipelineSectionDialog: false,
      chatExpanded: false,
      unsubscribe: (() => {}) as () => void,
      clockInterval: null as ReturnType<typeof setInterval> | null,
      worksheetDrawerOpen: false,
      worksheetMobileSheetOpen: false,
      worksheetContentMounted: false,
      worksheetDrawerWidthOverride: null as number | null,
      worksheetDrawerResize: null as null | {
        pointerId: number;
        pointerX: number;
        width: number;
      },
    };
  },
  computed: {
    drawerWidth(): number {
      if (this.$q.screen.lt.sm) {
        return Math.min(300, this.$q.screen.width - 56);
      }
      return 350;
    },
    leftDrawerCurrentWidth(): number {
      return leftDrawerCurrentWidth(this.drawerMini, this.drawerWidth);
    },
    worksheetDrawerDefaultWidth(): number {
      if (this.$q.screen.lt.md) {
        return Math.min(520, this.$q.screen.width - 24);
      }
      return Math.min(560, Math.max(440, this.$q.screen.width * 0.4));
    },
    worksheetDrawerMinimumWidth(): number {
      return TRACKING_WORKSHEET_DRAWER_MIN_WIDTH;
    },
    worksheetDrawerMaximumWidth(): number {
      const maxWidthForViewport = Math.min(
        Math.floor(this.$q.screen.width * 0.6),
        this.$q.screen.width - TRACKING_WORKSHEET_DRAWER_MIN_MAP_WIDTH
      );
      return Math.max(this.worksheetDrawerMinimumWidth, maxWidthForViewport);
    },
    worksheetDrawerWidth(): number {
      const requestedWidth =
        this.worksheetDrawerWidthOverride ?? this.worksheetDrawerDefaultWidth;
      return this.clampWorksheetDrawerWidth(requestedWidth);
    },
    isWorksheetDrawerMobile(): boolean {
      return this.$q.screen.lt.md;
    },
    isWorksheetDrawerOpen(): boolean {
      return !this.isWorksheetDrawerMobile && this.worksheetDrawerOpen;
    },
    pigProgressCardOffset(): number {
      return mapCenterOffset({
        isMobileLayout: this.isWorksheetDrawerMobile,
        // Only while the drawer takes layout space; below its breakpoint it
        // overlays the map (QDrawer on-layout, not $q.screen).
        leftDrawerOpen: this.leftDrawerOpen && this.leftDrawerOnLayout,
        leftDrawerWidth: this.leftDrawerCurrentWidth,
        tracksheetOpen: this.isWorksheetDrawerOpen,
        tracksheetWidth: this.worksheetDrawerWidth,
        viewportWidth: this.$q.screen.width,
        cardWidth: this.pigProgressCardWidth,
      });
    },
    trackingWorksheetToggleStyle(): { right: string } {
      return {
        right: this.$q.screen.lt.sm
          ? '8px'
          : this.isWorksheetDrawerOpen
          ? `${this.worksheetDrawerWidth + 12}px`
          : '12px',
      };
    },
    activePigRuns(): any[] {
      return this.$store.getters.activePigRunsList;
    },
    activePigRunsFiltered(): any[] {
      const allRuns: any[] = this.activePigRuns;
      const filteredPigRuns: any[] = [];
      if (hasAnyRole(this.$store.state.user, 'viewer')) {
        if (
          this.$store.state.activePigRunArrayWithNotification != undefined &&
          this.$store.state.activePigRunArrayWithNotification.length > 0
        ) {
          for (let i = 0; i < allRuns.length; i++) {
            if (
              this.$store.state.activePigRunArrayWithNotification.findIndex(
                (element: number) => element == allRuns[i].pigRunId
              ) != -1
            ) {
              filteredPigRuns.push(allRuns[i]);
            }
          }
          return filteredPigRuns;
        } else {
          return filteredPigRuns;
        }
      }
      return allRuns;
    },
    devices(): any[] {
      return this.$store.state.device.devices;
    },
    selectedPigRun(): any | null {
      return this.selectedPigRunEntry?.pigRun ?? null;
    },
    selectedPigRunEntry(): PigRunMapEntry | null {
      const selectedId = this.$store.state.pigRun.selectedPigRunId;
      if (selectedId === null) return null;
      return (
        (this.$store.state.pigRun.pigRuns as Record<number, PigRunMapEntry>)[
          selectedId
        ] ?? null
      );
    },
    selectedCompletedPigRunId(): number | null {
      return this.$store.state.pigRun.selectedCompletedPigRunId;
    },
    queryParams(): any {
      return this.$store.state.queryParams;
    },
    user(): any {
      return this.$store.state.user;
    },
    openStreamUids(): number[] {
      return this.$store.state.realtimeStreaming.openStreamUids;
    },
    activeStreamUid(): number | null {
      return this.$store.state.realtimeStreaming.activeStreamUid;
    },
  },
  watch: {
    isWorksheetDrawerMobile() {
      this.closeWorksheetForViewport();
    },
  },
  created() {
    this.unsubscribe = this.$store.subscribeAction(
      (action: any, state: any) => {
        if (action.type === 'notify') {
          this.notify(action.payload);
        }
        if (action.type === 'pigRunsReady') {
          this.handleQueryParams();
        }
        if (
          action.type === 'realtimeStreamerShow' ||
          action.type === 'openAdditionalStream'
        ) {
          if (action.payload) {
            const uid = action.payload;
            // If this UID is already open (e.g. switching from playback to
            // live), let RealTimeStream handle the switch internally — don't
            // remove/re-add the UID which would destroy & re-create the
            // component and cause a leave request.
            if (!this.openStreamUids.includes(uid)) {
              // Close any existing stream before opening the new one
              for (const existingUid of this.openStreamUids) {
                this.$store.commit('REMOVE_OPEN_STREAM_UID', existingUid);
              }
              this.$store.commit('ADD_OPEN_STREAM_UID', uid);
            }
          }
        }
        if (action.type === 'openPlayback') {
          const { streamRecordingId, uid, startedDateUtc } = action.payload;
          // If this UID is already open (live or playback), let
          // RealTimeStream handle the switch internally via its action
          // subscription — avoid destroying/re-creating the component.
          if (this.openStreamUids.includes(uid)) {
            // Store pending playback so the component can read it
            this.$store.commit('SET_PENDING_PLAYBACK', {
              uid,
              streamRecordingId,
              startedDateUtc,
            });
          } else {
            // Different UID: close existing streams, open new one
            for (const existingUid of this.openStreamUids) {
              this.$store.commit('REMOVE_OPEN_STREAM_UID', existingUid);
            }
            this.$store.commit('SET_PENDING_PLAYBACK', {
              uid,
              streamRecordingId,
              startedDateUtc,
            });
            this.$store.commit('ADD_OPEN_STREAM_UID', uid);
          }
        }
        if (action.type === 'closeStream') {
          if (action.payload) {
            this.$store.commit('REMOVE_OPEN_STREAM_UID', action.payload);
          }
        }
        if (action.type === 'streamSessionUpdateReceived') {
          const msg = action.payload;
          if (
            msg &&
            !msg.joined &&
            msg.userId === this.$store.state.user?.id &&
            this.openStreamUids.includes(msg.uid)
          ) {
            this.$store.commit('REMOVE_OPEN_STREAM_UID', msg.uid);
          }
        }
      }
    );

    this.$store.dispatch('getDevices');

    const unwatch = this.$watch('devices', (newVal: any) => {
      if (newVal) {
        setTimeout(() => {
          // Skip if pig runs were already loaded (e.g. by a SignalR event)
          const pigRuns = this.$store.state.pigRun.pigRuns;
          if (!pigRuns || Object.keys(pigRuns).length === 0) {
            this.$store.dispatch('getActivePigRuns');
          }
          this.devicesReady = true;
        }, 1000);
        unwatch();
      }
    });

    this.$store.dispatch('getDefaultNotificationSettings');
    this.$store.dispatch('fetchActiveStreamSessions');
    this.$store.dispatch(
      'getActivePigRunListForUserWithNotification',
      this.$store.state.user.id
    );
    this.$store.dispatch(
      'getActivePigRunUidListForUserWithNotification',
      this.$store.state.user.id
    );
  },
  mounted() {
    this.updateClock();
    this.clockInterval = setInterval(() => this.updateClock(), 1000);
  },
  beforeUnmount() {
    this.unsubscribe();
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
    this.endWorksheetDrawerResize();
  },
  methods: {
    updateClock() {
      const el = this.$refs.utcClockText as HTMLElement | undefined;
      if (el) {
        el.textContent = `Your local time in UTC: ${new Date()
          .toISOString()
          .slice(11, 19)}`;
      }
    },
    notify(message: any) {
      this.$q.notify(message);
    },
    onStreamClosed(uid: number) {
      this.$store.commit('REMOVE_OPEN_STREAM_UID', uid);
      this.$store.dispatch('leaveStreamSession', uid);
    },
    handleQueryParams() {
      if (!this.startupComplete) {
        if (this.$route.query && Object.keys(this.$route.query).length !== 0) {
          this.$store.dispatch('handleQueryParams', this.$route.query);
        } else if (
          this.queryParams &&
          Object.keys(this.queryParams).length !== 0
        ) {
          this.$store.dispatch('handleQueryParams', this.queryParams);
        }
        this.startupComplete = true;
      }
    },
    onLogout() {
      MessageHub.stop();
      StreamingHub.stop();
      GroupChatHub.stop();
      this.$store.dispatch('login', null);
      this.$router.push({ name: 'login' });
    },
    onPigRunManagerWizard() {
      this.expandDrawer();
      this.$store.dispatch('showPigRunManager');
    },
    onReportManagerClick() {
      this.expandDrawer();
      this.$store.dispatch('showReportManager');
    },
    onDownloadManagerClick() {
      this.expandDrawer();
      this.$store.dispatch('showDownloadManager');
    },
    onBroadcastCommandsClick() {
      this.expandDrawer();
      this.$store.dispatch('showBroadcastCommands');
    },
    onStreamManagerClick() {
      this.expandDrawer();
      this.$store.dispatch('showStreamManager');
      this.$store.dispatch('fetchActiveStreamSessions');
    },
    onUserManager() {
      if (hasAnyRole(this.user, 'superadmin')) {
        this.$router.push({ name: 'userManagement' });
      } else {
        this.$store.dispatch('localUserManagerShow');
      }
    },
    onGroupAssignment() {
      this.$router.push({ name: 'groupAssignment' });
    },
    onSystemMetrics() {
      this.$router.push({ name: 'metricsAdmin' });
    },
    onServiceStatus() {
      this.$router.push({ name: 'serviceStatus' });
    },
    onPipelineSectionManager() {
      this.managePipelineSectionDialog = true;
    },
    onChangePassword() {
      this.$store.dispatch('changePassword');
    },
    onNotificationSettings() {
      this.$store.dispatch('notificationSettings');
    },
    toggleWorksheetDrawer() {
      if (this.worksheetDrawerOpen) {
        this.closeWorksheetDrawer();
        return;
      }
      this.worksheetContentMounted = true;
      this.worksheetDrawerOpen = true;
    },
    closeWorksheetDrawer() {
      this.endWorksheetDrawerResize();
      this.worksheetDrawerOpen = false;
    },
    clampWorksheetDrawerWidth(width: number): number {
      return Math.min(
        Math.max(width, this.worksheetDrawerMinimumWidth),
        this.worksheetDrawerMaximumWidth
      );
    },
    startWorksheetDrawerResize(event: PointerEvent) {
      if (
        event.button !== 0 ||
        this.isWorksheetDrawerMobile ||
        !this.worksheetDrawerOpen
      ) {
        return;
      }

      this.worksheetDrawerResize = {
        pointerId: event.pointerId,
        pointerX: event.clientX,
        width: this.worksheetDrawerWidth,
      };
      window.addEventListener('pointermove', this.onWorksheetDrawerResizeMove);
      window.addEventListener('pointerup', this.endWorksheetDrawerResize);
      window.addEventListener('pointercancel', this.endWorksheetDrawerResize);
      event.preventDefault();
    },
    onWorksheetDrawerResizeMove(event: PointerEvent) {
      const resize = this.worksheetDrawerResize;
      if (!resize || event.pointerId !== resize.pointerId) {
        return;
      }

      this.worksheetDrawerWidthOverride = this.clampWorksheetDrawerWidth(
        resize.width + resize.pointerX - event.clientX
      );
    },
    endWorksheetDrawerResize(event?: PointerEvent) {
      if (
        event &&
        this.worksheetDrawerResize &&
        event.pointerId !== this.worksheetDrawerResize.pointerId
      ) {
        return;
      }

      this.worksheetDrawerResize = null;
      window.removeEventListener(
        'pointermove',
        this.onWorksheetDrawerResizeMove
      );
      window.removeEventListener('pointerup', this.endWorksheetDrawerResize);
      window.removeEventListener(
        'pointercancel',
        this.endWorksheetDrawerResize
      );
    },
    resetWorksheetDrawerWidth() {
      this.worksheetDrawerWidthOverride = null;
    },
    resizeWorksheetDrawerBy(delta: number) {
      this.worksheetDrawerWidthOverride = this.clampWorksheetDrawerWidth(
        this.worksheetDrawerWidth + delta
      );
    },
    onWorksheetDrawerResizeKeydown(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.resizeWorksheetDrawerBy(TRACKING_WORKSHEET_DRAWER_RESIZE_STEP);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.resizeWorksheetDrawerBy(-TRACKING_WORKSHEET_DRAWER_RESIZE_STEP);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.worksheetDrawerWidthOverride = this.worksheetDrawerMinimumWidth;
      } else if (event.key === 'End') {
        event.preventDefault();
        this.worksheetDrawerWidthOverride = this.worksheetDrawerMaximumWidth;
      }
    },
    toggleMobileWorksheetSheet() {
      if (this.worksheetMobileSheetOpen) {
        this.closeMobileWorksheetSheet();
        return;
      }
      this.worksheetContentMounted = true;
      this.worksheetMobileSheetOpen = true;
      this.$nextTick(() => this.$store.state.map?.invalidateSize());
    },
    closeMobileWorksheetSheet() {
      this.worksheetMobileSheetOpen = false;
      this.$nextTick(() => this.$store.state.map?.invalidateSize());
    },
    onTrackingWorksheetDrawerHide() {
      this.endWorksheetDrawerResize();
      this.worksheetContentMounted = false;
    },
    onTrackingWorksheetMobileSheetHide() {
      this.worksheetContentMounted = false;
    },
    closeWorksheetForViewport() {
      this.endWorksheetDrawerResize();
      this.worksheetDrawerOpen = false;
      this.worksheetMobileSheetOpen = false;
      this.worksheetContentMounted = false;
      this.$nextTick(() => this.$store.state.map?.invalidateSize());
    },
    onShowMarkerOnMap(payload: SelectedMarker) {
      if (this.isWorksheetDrawerMobile) {
        this.closeMobileWorksheetSheet();
      } else {
        this.closeWorksheetDrawer();
      }
      this.$nextTick(() => {
        this.$store.dispatch('goToLatLon', payload);
        this.$store.dispatch('setSelectedMarker', payload);
      });
    },
    drawerToggle() {
      this.$store.dispatch('drawerToggle');
    },
    openDrawer() {
      this.drawerMini = false;
      this.leftDrawerOpen = true;
      this.$nextTick(() => {
        this.$store.state.map?.invalidateSize();
      });
    },
    expandDrawer() {
      this.leftDrawerOpen = true;
      if (this.drawerMini) {
        this.drawerMini = false;
        this.$nextTick(() => {
          this.$store.state.map?.invalidateSize();
        });
      }
    },
    toggleDrawer() {
      if (this.$q.screen.lt.md) {
        this.leftDrawerOpen = false;
      } else {
        this.drawerMini = !this.drawerMini;
      }
      this.$nextTick(() => {
        this.$store.state.map?.invalidateSize();
      });
    },
    userHasAnyRole(...roles: string[]): boolean {
      return hasAnyRole(this.user, ...roles);
    },
    userHasAllRoles(...roles: string[]): boolean {
      return hasAllRoles(this.user, ...roles);
    },
  },
});
</script>

<style>
.text-blue-brand {
  color: #24337a !important;
}
.bg-blue-brand {
  background: #24337a;
}
.text-grey-brand {
  color: #575c55 !important;
}
.bg-grey-brand {
  background: #575c55;
}
.text-orange-brand {
  color: #f26921 !important;
}
.bg-orange-brand {
  background: #f26921;
}
.btn-brand {
  color: white !important;
  background: #575c55 !important;
}
.btn-brand:hover {
  background: #f26921 !important;
}

.tracking-worksheet-toggle-overlay {
  position: fixed;
  top: 58px;
  z-index: 1600;
  transition: right 0.2s ease;
}

.tracking-worksheet-toggle {
  border: 1px solid #c9d1df;
  border-radius: 6px;
  box-shadow: 0 2px 7px rgb(0 0 0 / 20%);
  min-height: 34px;
}

.tracking-worksheet-drawer {
  position: relative;
}

.tracking-worksheet-resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  width: 8px;
  border-left: 2px solid transparent;
  cursor: col-resize;
  touch-action: none;
  outline: none;
}

.tracking-worksheet-resize-handle:hover,
.tracking-worksheet-resize-handle:focus-visible,
.tracking-worksheet-resize-handle--active {
  border-left-color: #1976d2;
  background: rgb(25 118 210 / 10%);
}

.tracking-worksheet-sheet {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
}

.tracking-worksheet-mobile-header {
  flex: 0 0 auto;
  min-height: 42px;
  border-bottom: 1px solid #d6dce5;
  background: #fff;
}

.tracking-worksheet-mobile-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

/* Wrapper fills the Quasar drawer scroll container and
   uses flex-column so GroupChat pins to the bottom. */
.drawer-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.drawer-panels {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

/* Push Leaflet zoom/home controls below the pig progress card on mobile */
@media (max-width: 599px) {
  .leaflet-top.leaflet-left {
    top: 40px;
  }
}
</style>
